import React, { useEffect, useState, useMemo } from 'react';
import { Collapse, FormControl, MenuItem, TextField, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { stringifyJSONAttributes, stringToBooleanConversion } from '../../../../../utils/utilFunctions';
import WorkflowSessionModalButton from './WorkflowSessionModalButton';
import WorkflowSessionModal from '../../../../ItemProperties/WorkflowSessionModal';
import TPCheckbox from '../../../../TemplateProperties/TPCheckbox';
import ManualOrRowInput, { rowsToObject } from './ManualOrRowInput';

export const JWT_ADD_TO_OPTIONS = [
    { value: 'REQUEST_HEADER', labelKey: 'JWT_ADD_TO_REQUEST_HEADER' },
    { value: 'QUERY_PARAMETER', labelKey: 'JWT_ADD_TO_QUERY_PARAMETER' },
];

export const JWT_ALGORITHM_OPTIONS = [
    'HS256', 'HS384', 'HS512',
    'RS256', 'RS384', 'RS512',
    'PS256', 'PS384', 'PS512',
    'ES256', 'ES384', 'ES512',
];

const getDefaultPayloadRows = (algorithm) => [
    { key: 'alg', value: algorithm || 'HS256' },
    { key: 'typ', value: 'jwt' },
    { key: 'kid', value: '' },
];

const DEFAULT_JWT_HEADER_ROWS = [
    { key: 'iat', value: '' },
    { key: 'client_id', value: '' },
    { key: 'audience', value: '' },
    { key: 'private_key', value: '' },
    { key: 'scope', value: '' },
];

const buildUpdatedPayload = (currentPayloadJson, newAlg) => {
    try {
        const parsed = JSON.parse(currentPayloadJson);
        const rows = Object.entries(parsed).map(([k, v]) =>
            k === 'alg' ? { key: k, value: newAlg } : { key: k, value: v ?? '' }
        );
        return JSON.stringify(rowsToObject(rows));
    } catch {
        return JSON.stringify(rowsToObject(getDefaultPayloadRows(newAlg)));
    }
};

const JwtBearerFields = ({
    connectionDetails,
    repoPDC,
    useConnection,
    disableControl,
    screen,
    onInputChange,
}) => {
    const { t } = useTranslation();
    
    const [jwtAlgorithm, setJwtAlgorithm] = useState('HS256');
    const [jwtSecret, setJwtSecret] = useState('');
    const [jwtPayload, setJwtPayload] = useState(connectionDetails?.JWT_PAYLOAD || '');
    const [jwtRequestHeaderPrefix, setJwtRequestHeaderPrefix] = useState(connectionDetails?.JWT_REQUEST_HEADER_PREFIX || '');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [jwtSecretBase64Encoded, setJwtSecretBase64Encoded] = useState(false);
    const [valueLabel, setValueLabel] = useState('');
    const [workflowSessionModal, setWorkflowSessionModal] = useState({ open: false, isFirstTime: false });

    const getFontWeight = (control, currentValue) => {
        if (!repoPDC) return 'normal';
        return repoPDC?.[control] !== currentValue && useConnection === 'preDefined' ? 'bold' : 'normal';
    };

    const fontWeights = useMemo(() => ({
        jwtAlgorithm: getFontWeight('JWT_ALGORITHM', jwtAlgorithm),
        jwtSecret: getFontWeight('JWT_SECRET', jwtSecret),
        jwtRequestHeaderPrefix: getFontWeight('JWT_REQUEST_HEADER_PREFIX', jwtRequestHeaderPrefix),
    }), [jwtAlgorithm, jwtSecret, jwtRequestHeaderPrefix, repoPDC, useConnection]);

    const workflowMappings = {
        [t('JWT_ALGORITHM')]: { stateSetter: setJwtAlgorithm, key: 'JWT_ALGORITHM', value: jwtAlgorithm },
        [t('JWT_SECRET')]: { stateSetter: setJwtSecret, key: 'JWT_SECRET', value: jwtSecret },
        [t('JWT_REQUEST_HEADER_PREFIX')]: { stateSetter: setJwtRequestHeaderPrefix, key: 'JWT_REQUEST_HEADER_PREFIX', value: jwtRequestHeaderPrefix },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            stateSetter(value);
            if (key === 'JWT_ALGORITHM') {
                onInputChange(null, null, {
                    JWT_ALGORITHM: value,
                    JWT_PAYLOAD: buildUpdatedPayload(jwtPayload, value),
                });
            } else {
                onInputChange(key, value);
            }
        }
    };

    useEffect(() => {
        if (!connectionDetails) return;
        setJwtAlgorithm(connectionDetails.JWT_ALGORITHM || 'HS256');
        setJwtSecret(connectionDetails.JWT_SECRET || '');
        setJwtPayload(connectionDetails.JWT_PAYLOAD || '');
        setJwtRequestHeaderPrefix(connectionDetails.JWT_REQUEST_HEADER_PREFIX || '');
        setJwtSecretBase64Encoded(stringToBooleanConversion(connectionDetails.JWT_SECRET_BASE64_ENCODED) || false);
    }, [connectionDetails]);

    // Passed to ManualOrRowInput so the 'alg' row stays in sync with the algorithm dropdown
    const payloadSyncValues = useMemo(() => ({ alg: jwtAlgorithm }), [jwtAlgorithm]);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Algorithm */}
                <div style={{ display: 'flex', gap: '4px' }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={t('JWT_ALGORITHM')}
                            value={jwtAlgorithm || 'HS256'}
                            disabled={disableControl || false}
                            inputProps={{ style: { fontWeight: fontWeights.jwtAlgorithm } }}
                            onChange={(e) => {
                                const newAlg = e.target.value;
                                setJwtAlgorithm(newAlg);
                                onInputChange(null, null, {
                                    JWT_ALGORITHM: newAlg,
                                    JWT_PAYLOAD: buildUpdatedPayload(jwtPayload, newAlg),
                                });
                            }}
                            className="width-100"
                        >
                            {JWT_ALGORITHM_OPTIONS.map((alg) => (
                                <MenuItem key={alg} value={alg}>{alg}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                    {['GET_DATA', 'SEND_DATA'].includes(screen) && (
                        <WorkflowSessionModalButton
                            disableControl={disableControl || false}
                            label={t('JWT_ALGORITHM')}
                            setWorkflowSessionModal={setWorkflowSessionModal}
                            setValueLabel={setValueLabel}
                        />
                    )}
                </div>

                {/* Signing secret / PEM private key */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t('JWT_SECRET')}
                            value={jwtSecret}
                            disabled={disableControl || false}
                            multiline
                            minRows={2}
                            inputProps={{ style: { fontWeight: fontWeights.jwtSecret } }}
                            onChange={(e) => {
                                const val = e.target.value;
                                setJwtSecret(val);
                                onInputChange('JWT_SECRET', val);
                            }}
                            className="width-100"
                            autoComplete="off"
                        />
                    </FormControl>
                    {['GET_DATA', 'SEND_DATA'].includes(screen) && (
                        <WorkflowSessionModalButton
                            disableControl={disableControl || false}
                            label={t('JWT_SECRET')}
                            setWorkflowSessionModal={setWorkflowSessionModal}
                            setValueLabel={setValueLabel}
                        />
                    )}
                </div>

                {/* Secret Base64 Encoded */}
                <TPCheckbox
                    label={t('JWT_SECRET_BASE64_ENCODED')}
                    checked={jwtSecretBase64Encoded}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        setJwtSecretBase64Encoded(checked);
                        onInputChange('JWT_SECRET_BASE64_ENCODED', stringifyJSONAttributes(checked));
                    }}
                />

                {/* Payload — fully managed by ManualOrRowInput */}
                <ManualOrRowInput
                    key={connectionDetails?.CONNECTIONSTRING || 'default'}
                    label={t('JWT_PAYLOAD')}
                    defaultRows={getDefaultPayloadRows(jwtAlgorithm)}
                    defaultValue={connectionDetails?.JWT_PAYLOAD}
                    syncRowValues={payloadSyncValues}
                    addRowLabel={t('JWT_ADD_PAYLOAD')}
                    onChange={(val) => { setJwtPayload(val); onInputChange('JWT_PAYLOAD', val); }}
                    disableControl={disableControl || false}
                    screen={screen}
                />

                {/* Advanced Configuration */}
                <div>
                    <div
                        onClick={() => setAdvancedOpen((prev) => !prev)}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Typography variant="body2" fontWeight={500}>{t('JWT_ADVANCED_CONFIGURATION')}</Typography>
                        {advancedOpen ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </div>
                    <Collapse in={advancedOpen}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px' }}>

                            {/* Request Header Prefix */}
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    label={t('JWT_REQUEST_HEADER_PREFIX')}
                                    value={jwtRequestHeaderPrefix}
                                    disabled={disableControl || false}
                                    inputProps={{ style: { fontWeight: fontWeights.jwtRequestHeaderPrefix } }}
                                    onChange={(e) => {
                                        setJwtRequestHeaderPrefix(e.target.value);
                                        onInputChange('JWT_REQUEST_HEADER_PREFIX', e.target.value);
                                    }}
                                    className="width-100"
                                />
                                {['GET_DATA', 'SEND_DATA'].includes(screen) && (
                                    <WorkflowSessionModalButton
                                        disableControl={disableControl || false}
                                        label={t('JWT_REQUEST_HEADER_PREFIX')}
                                        setWorkflowSessionModal={setWorkflowSessionModal}
                                        setValueLabel={setValueLabel}
                                    />
                                )}
                            </div>

                            {/* JWT Headers */}
                            <ManualOrRowInput
                                key={connectionDetails?.CONNECTIONSTRING || 'default'}
                                label={t('JWT_HEADERS')}
                                defaultRows={DEFAULT_JWT_HEADER_ROWS}
                                defaultValue={connectionDetails?.JWT_HEADERS}
                                addRowLabel={t('JWT_ADD_HEADER')}
                                onChange={(val) => onInputChange('JWT_HEADERS', val)}
                                disableControl={disableControl || false}
                                screen={screen}
                            />
                        </div>
                    </Collapse>
                </div>
            </div>

            {/* WorkflowSessionModal for this component's own fields (Algorithm) */}
            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() => setWorkflowSessionModal({ open: false, isFirstTime: false })}
                initialValue={workflowMappings[valueLabel]?.value}
                inputType="text"
                valuesHandler={valuesHandler}
            />
        </>
    );
};

export default JwtBearerFields;
